import React, { useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { motion } from "framer-motion";
import { UiClue } from "@/utils/crosswordUtils";
import { ArrowRight, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface CluesProps {
  acrossClues: UiClue[];
  downClues: UiClue[];
  activeClueNumber: number | null;
  direction: 'across' | 'down';
  onClueSelect: (row: number, col: number, direction: 'across' | 'down') => void;
}

const CrosswordClues: React.FC<CluesProps> = ({
  acrossClues,
  downClues,
  activeClueNumber,
  direction,
  onClueSelect,
}) => {
  const acrossScrollRef = useRef<HTMLDivElement>(null);
  const downScrollRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [tabChanged, setTabChanged] = React.useState(false);

  useEffect(() => {
    if (!activeClueNumber) return;

    const timer = setTimeout(() => {
      // Find the active button
      const activeButton = document.querySelector(
        `[data-clue-number="${activeClueNumber}"][data-direction="${direction}"]`
      ) as HTMLButtonElement;

      if (activeButton) {
        // Store the active button reference
        activeButtonRef.current = activeButton;
        
        // Use requestAnimationFrame to ensure both operations happen in the same frame
        requestAnimationFrame(() => {
          // Add the active class
          activeButton.classList.add('bg-secondary');
          
          // Only scroll on non-mobile devices
          if (window.innerWidth > 768) {
            activeButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    }, 10);

    return () => {
      clearTimeout(timer);
      if (activeButtonRef.current) {
        activeButtonRef.current.classList.remove('bg-secondary');
      }
    };
  }, [activeClueNumber, direction, tabChanged]);

  const handleDirectionChange = (newDirection: 'across' | 'down') => {
    if (direction !== newDirection && activeClueNumber) {
      const clues = newDirection === 'across' ? acrossClues : downClues;
      const clue = clues.find(clue => clue.number === activeClueNumber);
      if (clue) {
        onClueSelect(clue.row, clue.col, newDirection);
      } else if (clues.length > 0) {
        onClueSelect(clues[0].row, clues[0].col, newDirection);
      }
    }
    setTabChanged(prev => !prev);
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="relative z-50">
          {/* Liquid glass nav bar container */}
          <div
            className={
              cn(
                "flex items-center py-1 px-1 rounded-full shadow-lg border border-black/40 bg-white/30 dark:bg-black/30 backdrop-blur-md",
                "before:absolute before:inset-0 before:rounded-full before:pointer-events-none before:z-[-1]",
                "before:bg-gradient-to-br before:from-white/40 before:to-white/10 before:dark:from-white/10 before:dark:to-black/20",
                "before:shadow-[0_0_6px_rgba(0,0,0,0.03),0_2px_6px_rgba(0,0,0,0.08),inset_3px_3px_0.5px_-3px_rgba(0,0,0,0.09),inset_-3px_-3px_0.5px_-3px_rgba(0,0,0,0.08),inset_1px_1px_1px_-0.5px_rgba(0,0,0,0.06),inset_-1px_-1px_1px_-0.5px_rgba(0,0,0,0.06),inset_0_0_6px_6px_rgba(0,0,0,0.12),inset_0_0_2px_2px_rgba(0,0,0,0.06),0_0_12px_rgba(255,255,255,0.15)]"
              )
            }
            style={{ position: 'relative', overflow: 'hidden' }}
          >
            {/* Across and Down buttons */}
            <div className="flex items-center flex-1">
              <button
                onClick={() => handleDirectionChange('across')}
                className={cn(
                  "relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-full transition-colors flex items-center gap-2 w-full justify-center",
                  "text-foreground/80 hover:text-primary",
                  direction === 'across' && "bg-muted text-primary",
                )}
              >
                <ArrowRight size={18} strokeWidth={2.5} />
                <span>Across</span>
                {direction === 'across' && (
                  <motion.div
                    layoutId="lamp"
                    className="absolute inset-0 w-full bg-primary/5 rounded-full -z-10"
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                      <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                      <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-sm -top-1" />
                      <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                    </div>
                  </motion.div>
                )}
              </button>
            </div>
            <div className="flex items-center flex-1">
              <button
                onClick={() => handleDirectionChange('down')}
                className={cn(
                  "relative cursor-pointer text-sm font-semibold px-6 py-2 rounded-full transition-colors flex items-center gap-2 w-full justify-center",
                  "text-foreground/80 hover:text-primary",
                  direction === 'down' && "bg-muted text-primary",
                )}
              >
                <ArrowDown size={18} strokeWidth={2.5} />
                <span>Down</span>
                {direction === 'down' && (
                  <motion.div
                    layoutId="lamp"
                    className="absolute inset-0 w-full bg-primary/5 rounded-full -z-10"
                    initial={false}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                  >
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-1 bg-primary rounded-t-full">
                      <div className="absolute w-12 h-6 bg-primary/20 rounded-full blur-md -top-2 -left-2" />
                      <div className="absolute w-8 h-6 bg-primary/20 rounded-full blur-sm -top-1" />
                      <div className="absolute w-4 h-4 bg-primary/20 rounded-full blur-sm top-0 left-2" />
                    </div>
                  </motion.div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {direction === 'across' ? (
        <ScrollArea className="h-[300px] pr-4" ref={acrossScrollRef}>
          <div className="space-y-1">
            {acrossClues.map((clue) => (
              <Button
                key={`across-${clue.number}`}
                variant="ghost"
                className="w-full justify-start text-left"
                onClick={() => onClueSelect(clue.row, clue.col, 'across')}
                data-clue-number={clue.number}
                data-direction="across"
              >
                <span className="font-medium mr-2">{clue.number}.</span> {clue.clue}
              </Button>
            ))}
          </div>
        </ScrollArea>
      ) : (
        <ScrollArea className="h-[300px] pr-4" ref={downScrollRef}>
          <div className="space-y-1">
            {downClues.map((clue) => (
              <Button
                key={`down-${clue.number}`}
                variant="ghost"
                className="w-full justify-start text-left"
                onClick={() => onClueSelect(clue.row, clue.col, 'down')}
                data-clue-number={clue.number}
                data-direction="down"
              >
                <span className="font-medium mr-2">{clue.number}.</span> {clue.clue}
              </Button>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

export default CrosswordClues;